import { isServer } from '@/helpers';
import { DealsSearchState } from '@/reducers/dealsSearchReducer';

/**
 * Per-tab cache for deals search results state.
 *
 * Allows restoring the SERP (including all "load more" pages, scroll position,
 * filters, etc.) when the user clicks a card to view a deal/place detail and
 * then navigates back via the browser back button.
 */

const CACHE_KEY = 'dealsSearchStateCache';
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

type DealsSearchStateCache = {
  state: DealsSearchState;
  scrollPosition: number;
  url: string;
  savedAt: number;
};

export function saveDealsSearchStateToCache(state: DealsSearchState): void {
  if (isServer) return;
  try {
    const payload: DealsSearchStateCache = {
      state,
      scrollPosition: window.scrollY,
      url: window.location.pathname + window.location.search,
      savedAt: Date.now(),
    };
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(payload));
  } catch {
    clearDealsSearchStateCache();
  }
}

export function clearDealsSearchStateCache(): void {
  if (isServer) return;
  try {
    sessionStorage.removeItem(CACHE_KEY);
  } catch {
    /* noop */
  }
}

export function readDealsSearchStateCache(): DealsSearchStateCache | null {
  if (isServer) return null;
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DealsSearchStateCache;
    if (!parsed || !parsed.state || !parsed.url || !parsed.savedAt) {
      clearDealsSearchStateCache();
      return null;
    }
    if (Date.now() - parsed.savedAt > CACHE_DURATION_MS) {
      clearDealsSearchStateCache();
      return null;
    }
    return parsed;
  } catch {
    clearDealsSearchStateCache();
    return null;
  }
}

/**
 * Read the cached state if it matches the current URL.
 * The cache is intentionally NOT removed so that multiple back/forward
 * navigations to the same SERP URL keep restoring the state. It is
 * invalidated automatically by URL mismatch on a new search or by TTL.
 */
export function consumeDealsSearchStateForCurrentUrl(): DealsSearchStateCache | null {
  if (isServer) return null;
  const cached = readDealsSearchStateCache();
  if (!cached) return null;
  const currentUrl = window.location.pathname + window.location.search;
  if (cached.url !== currentUrl) {
    clearDealsSearchStateCache();
    return null;
  }
  return cached;
}
